import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Row, Col, Alert } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';

const ApplicantForm = () => {
  const { atsNumber } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email_id: '',
    country_code: '',
    phone_number: '',
    domain: '',
    source: '',
    application_date: null,
    meet_greet_date: null,
    location: '',
    resume_link: '',
    attended_meet_greet: false,
    registered_startupworld: false,
    assigned_project: false,
    rejected: false,
    left: false
  });

  const isEditMode = !!atsNumber;

  useEffect(() => {
    if (isEditMode) {
      fetchApplicant();
    }
  }, [atsNumber]);

  const fetchApplicant = async () => {
    try {
      setLoading(true);
    const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  const response = await axios.get(`${base}/api/applicants/${atsNumber}`, { withCredentials: true });
      const applicant = response.data;
      
      setFormData({
        first_name: applicant.first_name || '',
        last_name: applicant.last_name || '',
        email_id: applicant.email_id || '',
        country_code: applicant.country_code || '',
        phone_number: applicant.phone_number || '',
        domain: applicant.domain || '',
        source: applicant.source || '',
        application_date: applicant.application_date ? new Date(applicant.application_date) : null,
        meet_greet_date: applicant.meet_greet_date ? new Date(applicant.meet_greet_date) : null,
        location: applicant.location || '',
        resume_link: applicant.resume_link || '',
        attended_meet_greet: applicant.attended_meet_greet || false,
        registered_startupworld: applicant.registered_startupworld || false,
        assigned_project: applicant.assigned_project || false,
        rejected: applicant.rejected || false,
        left: applicant.left || false
      });
    } catch (error) {
      console.error('Error fetching applicant:', error);
      toast.error('Failed to load applicant data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      
      const submitData = {
        ...formData,
        application_date: formData.application_date ? formData.application_date.toISOString().split('T')[0] : null,
        meet_greet_date: formData.meet_greet_date ? formData.meet_greet_date.toISOString().split('T')[0] : null
      };

      if (isEditMode) {
    const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  const response = await axios.put(`${base}/api/applicants/${atsNumber}`, submitData, { withCredentials: true });
        toast.success('Applicant updated successfully');
        console.log('Update response:', response.data);
      } else {
    const base = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  const response = await axios.post(`${base}/api/applicants`, submitData, { withCredentials: true });
        if (response.data.updated) {
          toast.success('Applicant updated (duplicate found)');
        } else {
          toast.success('Applicant created successfully');
        }
      }
      
      navigate('/applicants');
    } catch (error) {
      console.error('Error saving applicant:', error);
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Failed to save applicant');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="applicant-form-container">
      <div className="form-header">
        <h1>{isEditMode ? 'Edit Applicant' : 'Add New Applicant'}</h1>
        <p>{isEditMode ? 'Update applicant information' : 'Enter applicant details'}</p>
      </div>

      <Card className="form-card">
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              {/* Slot Number field removed */}
              <Col md={6}>
                <Form.Group className="mb-3 form-group">
                  <Form.Label>Domain</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.domain}
                    onChange={(e) => handleInputChange('domain', e.target.value)}
                    placeholder="Enter domain"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3 form-group">
                  <Form.Label>Source</Form.Label>
                  <Form.Select
                    value={formData.source}
                    onChange={(e) => handleInputChange('source', e.target.value)}
                  >
                    <option value="">Select source</option>
                    <option value="AICTE">AICTE</option>
                    <option value="LinkedIn">LinkedIn</option>
                    <option value="Instagram">Instagram</option>
                    <option value="Direct">Direct</option>
                    <option value="Referral">Referral</option>
                    <option value="Website">Website</option>
                    <option value="Other">Other</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3 form-group">
                  <Form.Label>First Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.first_name}
                    onChange={(e) => handleInputChange('first_name', e.target.value)}
                    placeholder="Enter first name"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3 form-group">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.last_name}
                    onChange={(e) => handleInputChange('last_name', e.target.value)}
                    placeholder="Enter last name"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3 form-group">
                  <Form.Label>Email *</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email_id}
                    onChange={(e) => handleInputChange('email_id', e.target.value)}
                    placeholder="Enter email address"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3 form-group">
                  <Form.Label>Country Code</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.country_code}
                    onChange={(e) => handleInputChange('country_code', e.target.value)}
                    placeholder="+1"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3 form-group">
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.phone_number}
                    onChange={(e) => handleInputChange('phone_number', e.target.value)}
                    placeholder="Enter phone number"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3 form-group">
                  <Form.Label>Application Date</Form.Label>
                  <DatePicker
                    selected={formData.application_date}
                    onChange={(date) => handleInputChange('application_date', date)}
                    className="form-control"
                    placeholderText="Select application date"
                    dateFormat="yyyy-MM-dd"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3 form-group">
                  <Form.Label>Meet & Greet Date</Form.Label>
                  <DatePicker
                    selected={formData.meet_greet_date}
                    onChange={(date) => handleInputChange('meet_greet_date', date)}
                    className="form-control"
                    placeholderText="Select meet & greet date"
                    dateFormat="yyyy-MM-dd"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3 form-group">
                  <Form.Label>Location</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    placeholder="Enter location"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3 form-group">
                  <Form.Label>Resume Link</Form.Label>
                  <Form.Control
                    type="url"
                    value={formData.resume_link}
                    onChange={(e) => handleInputChange('resume_link', e.target.value)}
                    placeholder="Enter resume link"
                  />
                </Form.Group>
              </Col>
            </Row>

            {isEditMode && (
              <Alert variant="info" className="mb-3">
                <h6>Status Updates</h6>
                <Row>
                  <Col md={3}>
                    <Form.Check
                      type="checkbox"
                      label="Attended Meet & Greet"
                      checked={formData.attended_meet_greet}
                      onChange={(e) => handleInputChange('attended_meet_greet', e.target.checked)}
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Check
                      type="checkbox"
                      label="Registered on StartupWorld"
                      checked={formData.registered_startupworld}
                      onChange={(e) => handleInputChange('registered_startupworld', e.target.checked)}
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Check
                      type="checkbox"
                      label="Assigned Project"
                      checked={formData.assigned_project}
                      onChange={(e) => handleInputChange('assigned_project', e.target.checked)}
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Check
                      type="checkbox"
                      label="Rejected"
                      checked={formData.rejected}
                      onChange={(e) => handleInputChange('rejected', e.target.checked)}
                    />
                  </Col>
                </Row>
                <Row className="mt-2">
                  <Col md={3}>
                    <Form.Check
                      type="checkbox"
                      label="Left Program"
                      checked={formData.left}
                      onChange={(e) => handleInputChange('left', e.target.checked)}
                    />
                  </Col>
                </Row>
              </Alert>
            )}

            <div className="form-actions">
              <Button 
                variant="secondary" 
                onClick={() => navigate('/applicants')}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button 
                variant="primary" 
                type="submit"
                disabled={saving}
              >
                {saving ? 'Saving...' : (isEditMode ? 'Update Applicant' : 'Create Applicant')}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      <style jsx>{`
        .applicant-form-container {
          padding: 24px;
          background-color: #f8f9fa;
          min-height: 100vh;
        }

        .form-header {
          margin-bottom: 24px;
          text-align: center;
        }

        .form-header h1 {
          color: #2c3e50;
          font-size: 28px;
          font-weight: 600;
          margin-bottom: 8px;
        }

        .form-header p {
          color: #6c757d;
          font-size: 16px;
          margin: 0;
        }

        .form-card {
          border: none;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          border-radius: 8px;
        }

        .form-card .card-body {
          padding: 32px;
        }

        .form-label {
          font-weight: 600;
          color: #495057;
          margin-bottom: 8px;
        }

        .form-control, .form-select {
          border: 1px solid #ced4da;
          border-radius: 6px;
          padding: 12px 16px;
          font-size: 14px;
          transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
        }

        .form-control:focus, .form-select:focus {
          border-color: #80bdff;
          outline: 0;
          box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        }

        .form-check-input:checked {
          background-color: #007bff;
          border-color: #007bff;
        }

        .btn-primary {
          background-color: #007bff;
          border-color: #007bff;
          padding: 12px 24px;
          font-weight: 500;
          border-radius: 6px;
        }

        .btn-primary:hover {
          background-color: #0056b3;
          border-color: #0056b3;
        }

        .btn-secondary {
          background-color: #6c757d;
          border-color: #6c757d;
          padding: 12px 24px;
          font-weight: 500;
          border-radius: 6px;
        }

        .btn-secondary:hover {
          background-color: #545b62;
          border-color: #545b62;
        }

        .form-actions {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
          margin-top: 24px;
          padding-top: 24px;
          border-top: 1px solid #dee2e6;
        }

        .required-field {
          color: #dc3545;
        }

        .form-section {
          background: #f8f9fa;
          padding: 20px;
          border-radius: 6px;
          margin-bottom: 24px;
        }

        .form-section h4 {
          color: #495057;
          font-size: 18px;
          font-weight: 600;
          margin-bottom: 16px;
        }

        /* Ensure react-datepicker inputs align and stretch like Bootstrap inputs */
        .react-datepicker-wrapper,
        .react-datepicker__input-container {
          width: 100%;
          display: block;
        }
        .react-datepicker__input-container input.form-control {
          width: 100%;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .applicant-form-container {
            padding: 16px;
          }

          .form-card .card-body {
            padding: 20px;
          }

          .form-actions {
            flex-direction: column;
          }

          .form-actions .btn {
            width: 100%;
          }
          
        }
      `}</style>
    </div>
  );
};

export default ApplicantForm;